import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useAuth } from '../../../hooks/useAuth';
import { useUserSave } from '../../../hooks/useUserSave';
import { analyticsService } from '../../../services/analyticsService';
import type {
  LibraryBlockedUsersSyncResult,
  LibraryBlockedUsersSyncStatus,
} from './useLibraryModBlockedSync';
import { useLibraryBlockedUsersSync } from './useLibraryModBlockedSync';
import type { LibraryDialog } from '../libraryRouteTypes';
import { toLibraryModSettingsState } from '../moderation/libraryModSettingsResolver';
import type { ModSettings, UserAuthFields } from '@airhead-games/shared/types';

export type PlayerAccessViewModel = {
  requireTwitchLoginChecked: boolean;
  blockedAndBannedChecked: boolean;
  showConnectTwitchRow: boolean;
  showBlockedUsersRow: boolean;
  blockedUsersBadgeLabel: string;
  syncIssueOpen: boolean;
  onRequireTwitchLoginToggle: (checked: boolean) => void;
  onBlockedAndBannedToggle: (checked: boolean) => void;
  onConnectTwitchRowClick: () => void;
  onBlockedUsersRowClick: () => void;
  onSyncIssueOpenChange: (open: boolean) => void;
  onRetrySync: () => Promise<LibraryBlockedUsersSyncResult>;
  onReconnectTwitch: () => void;
};

type UseLibraryModPlayerAccessParams = {
  user: UserAuthFields | null;
  dialog: LibraryDialog | null;
  openDialog: (dialog: LibraryDialog) => void;
};

export type LibraryModPlayerAccessController = {
  hasTwitchAuth: boolean;
  connectTwitchPrompted: boolean;
  syncStatus: LibraryBlockedUsersSyncStatus;
  modSettings: ModSettings;
  handleModScopeDenied: () => void;
  viewModel: PlayerAccessViewModel;
};

export function useLibraryModPlayerAccess({
  user,
  dialog,
  openDialog,
}: UseLibraryModPlayerAccessParams): LibraryModPlayerAccessController {
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  const [modSettings, setModSettings] = useState<ModSettings>(() =>
    toLibraryModSettingsState(user)
  );
  const [connectTwitchPrompted, setConnectTwitchPrompted] = useState(false);
  const [syncIssueOpen, setSyncIssueOpen] = useState(false);
  const hasTwitchAuth = !!user?.authTwitch?.userId;
  const hasModScopeGrant = searchParams.get('modScopeGranted') === 'true';
  const requireAuth = useCallback(() => {
    openDialog('login');
  }, [openDialog]);
  const { markDirty } = useUserSave({
    value: modSettings,
    setValue: setModSettings,
    toUpdate: (value) => ({
      modSettings: {
        twitchPlayersOnly: value.twitchPlayersOnly,
        disallowBlockedAndBanned: value.disallowBlockedAndBanned,
      },
    }),
    fromUser: toLibraryModSettingsState,
    errorMessage: 'Unable to save your player access settings.',
  });

  const { syncStatus, handleSyncBlockedUsers, resetBlockedUsersSyncStatus } =
    useLibraryBlockedUsersSync({
      user,
      hasModScopeGrant,
      dialog,
      setUser,
      openDialog,
    });

  useEffect(() => {
    if (hasTwitchAuth) {
      setConnectTwitchPrompted(false);
    }
  }, [hasTwitchAuth]);

  useEffect(() => {
    if (syncStatus !== 'error') {
      setSyncIssueOpen(false);
    }
  }, [syncStatus]);

  const promptConnectTwitch = useCallback(() => {
    setConnectTwitchPrompted(true);
    toast.error('Link your Twitch account in Connections to use this feature.');
  }, []);

  const handleRequireTwitchLoginToggle = useCallback(
    (checked: boolean) => {
      if (!user) {
        requireAuth();
        return;
      }

      if (checked && !hasTwitchAuth) {
        promptConnectTwitch();
        return;
      }

      analyticsService.trackPreferenceChanged({
        preference_type: 'twitchPlayersOnly',
        preference_value: checked,
        link_location: 'library_player_access',
      });

      setModSettings((current) => ({
        ...current,
        twitchPlayersOnly: checked,
        ...(checked ? {} : { disallowBlockedAndBanned: false }),
      }));
      markDirty();
    },
    [
      hasTwitchAuth,
      markDirty,
      promptConnectTwitch,
      requireAuth,
      setModSettings,
      user,
    ]
  );

  const handleBlockedAndBannedToggle = useCallback(
    (checked: boolean) => {
      if (!user) {
        requireAuth();
        return;
      }

      if (checked && !hasTwitchAuth) {
        promptConnectTwitch();
        return;
      }

      analyticsService.trackPreferenceChanged({
        preference_type: 'disallowBlockedAndBanned',
        preference_value: checked,
        link_location: 'library_blocked_users',
      });

      setModSettings((current) => ({
        ...current,
        disallowBlockedAndBanned: checked,
        ...(checked ? { twitchPlayersOnly: true } : {}),
      }));

      if (checked) {
        // Sync persists both flags + the synced list in one backend write
        // (and returns the updated user), so we don't markDirty here.
        void handleSyncBlockedUsers().then((result) => {
          if (result !== 'error') {
            return;
          }

          setModSettings((current) => ({
            ...current,
            disallowBlockedAndBanned: false,
          }));
        });
        return;
      }

      // Toggling OFF: no sync to piggyback on, so persist via updateUser.
      resetBlockedUsersSyncStatus();
      markDirty();
    },
    [
      handleSyncBlockedUsers,
      hasTwitchAuth,
      markDirty,
      promptConnectTwitch,
      requireAuth,
      resetBlockedUsersSyncStatus,
      setModSettings,
      user,
    ]
  );

  const handleBlockedUsersRowClick = useCallback(() => {
    if (syncStatus === 'error') {
      setSyncIssueOpen(true);
      return;
    }

    openDialog('blocked-users');
  }, [openDialog, syncStatus]);

  const handleRetrySync = useCallback(async () => {
    const result = await handleSyncBlockedUsers();
    if (result === 'synced') {
      setSyncIssueOpen(false);
      openDialog('blocked-users');
    }
    return result;
  }, [handleSyncBlockedUsers, openDialog]);

  const handleReconnectTwitch = useCallback(() => {
    setSyncIssueOpen(false);
    openDialog('connections');
  }, [openDialog]);

  const handleModScopeDenied = useCallback(() => {
    setModSettings((current) => ({
      ...current,
      disallowBlockedAndBanned: false,
    }));
    resetBlockedUsersSyncStatus();
    markDirty();
  }, [markDirty, resetBlockedUsersSyncStatus, setModSettings]);

  const blockedUsersCount = modSettings.blockedTwitchUsers.length;
  const blockedUsersBadgeLabel =
    syncStatus === 'syncing'
      ? 'Syncing…'
      : syncStatus === 'error'
        ? 'Unavailable'
        : `Synced · ${blockedUsersCount}`;
  const showConnectTwitchRow = !hasTwitchAuth && connectTwitchPrompted;
  const showBlockedUsersRow =
    hasTwitchAuth &&
    (modSettings.disallowBlockedAndBanned || syncStatus === 'error');

  const viewModel = useMemo<PlayerAccessViewModel>(
    () => ({
      requireTwitchLoginChecked: modSettings.twitchPlayersOnly,
      blockedAndBannedChecked: modSettings.disallowBlockedAndBanned,
      showConnectTwitchRow,
      showBlockedUsersRow,
      blockedUsersBadgeLabel,
      syncIssueOpen,
      onRequireTwitchLoginToggle: handleRequireTwitchLoginToggle,
      onBlockedAndBannedToggle: handleBlockedAndBannedToggle,
      onConnectTwitchRowClick: () => openDialog('connections'),
      onBlockedUsersRowClick: handleBlockedUsersRowClick,
      onSyncIssueOpenChange: setSyncIssueOpen,
      onRetrySync: handleRetrySync,
      onReconnectTwitch: handleReconnectTwitch,
    }),
    [
      blockedUsersBadgeLabel,
      handleBlockedAndBannedToggle,
      handleBlockedUsersRowClick,
      handleReconnectTwitch,
      handleRequireTwitchLoginToggle,
      handleRetrySync,
      modSettings.disallowBlockedAndBanned,
      modSettings.twitchPlayersOnly,
      openDialog,
      showBlockedUsersRow,
      showConnectTwitchRow,
      syncIssueOpen,
    ]
  );

  return useMemo(
    () => ({
      hasTwitchAuth,
      connectTwitchPrompted,
      syncStatus,
      modSettings,
      handleModScopeDenied,
      viewModel,
    }),
    [
      connectTwitchPrompted,
      handleModScopeDenied,
      hasTwitchAuth,
      modSettings,
      syncStatus,
      viewModel,
    ]
  );
}
